import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useForm } from 'react-hook-form';

import OnGoLayout from '../OnGoLayout';
import OnGoQueuePreview from '../Preview/OnGoQueuePreview';
import OnGoInitializeFormContent from './OnGoInitializeFormContent';
import { ONGO_INIT_FORM_ID } from './onGoInitialize.constants';
import type { OnGoInitializeFormValues, OnGoInitializeProps } from './onGoInitialize.types';
import { resolveOnGoInitializeDefaultValues, saveOnGoInitializeFormValues } from './onGoInitializePersist';
import useSubjectStore from 'storages/subject';
import { useMutation, useQueryClient } from 'react-query';
import exerciseQueuesApi, { exerciseQueuesApiKeys } from 'api/rest/exerciseQueues/exerciseQueues';
import type { ExerciseQueue } from 'api/rest/exerciseQueues/exerciseQueues.types';
import { useUserLogContext } from 'context/UserLogContext/UserLogContext';
import { ON_GO_ACCESS } from 'constants/onGoAccess.config';
import useOnGoAccess from 'hooks/useOnGoAccess/useOnGoAccess';
import { resolveQueueErrorMessage } from './resolveQueueErrorMessage';
import FormErrorBanner from './FormErrorBanner';

export type { OnGoInitializeFormValues } from './onGoInitialize.types';

type InitializeStep = 'configure' | 'preview';

const OnGoInitialize: FC<OnGoInitializeProps> = ({ sectionsData }) => {
  const [step, setStep] = useState<InitializeStep>('configure');
  const [previewQueue, setPreviewQueue] = useState<ExerciseQueue | null>(null);
  const [submitBlockedUntilValid, setSubmitBlockedUntilValid] = useState(false);
  const [previewErrorMessage, setPreviewErrorMessage] = useState<string | null>(null);

  const { subject } = useSubjectStore();
  const { hasPremiumAccess } = useOnGoAccess();
  const isFreePlan = !hasPremiumAccess;

  const clampFormValuesForPlan = useCallback(
    (values: OnGoInitializeFormValues): OnGoInitializeFormValues => {
      if (!isFreePlan) {
        return values;
      }
      return {
        ...values,
        queueSize: Math.min(values.queueSize, ON_GO_ACCESS.freeMaxQueueSize),
        useAllExercises: false,
      };
    },
    [isFreePlan],
  );

  const defaultValues = useMemo(
    () => clampFormValuesForPlan(resolveOnGoInitializeDefaultValues(subject?.pk, sectionsData)),
    [clampFormValuesForPlan, subject?.pk, sectionsData],
  );

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    reset,
    watch,
    formState: { isValid },
  } = useForm<OnGoInitializeFormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues,
  });

  const queryClient = useQueryClient();
  const { logEvent } = useUserLogContext();

  useEffect(() => {
    reset(clampFormValuesForPlan(resolveOnGoInitializeDefaultValues(subject?.pk, sectionsData)));
  }, [clampFormValuesForPlan, reset, sectionsData, subject?.pk]);

  useEffect(() => {
    if (!subject?.pk) {
      return undefined;
    }

    const subscription = watch(values => {
      saveOnGoInitializeFormValues(subject.pk, values as OnGoInitializeFormValues);
    });

    return () => subscription.unsubscribe();
  }, [subject?.pk, watch]);

  const createQueueMutation = useMutation(exerciseQueuesApi.create(), {
    onSuccess: queue => {
      if (subject) {
        logEvent('on-go-queue-create', {
          subjectPk: subject.pk,
          subjectCode: subject.subjectCode,
          queueUuid: queue.uuid,
          sort: getValues('sort'),
          size: getValues('useAllExercises') ? null : getValues('queueSize'),
        });
      }
      setPreviewQueue(queue);
      setStep('preview');
      setPreviewErrorMessage(null);
    },
  });

  const closeQueueMutation = useMutation((queueUuid: ExerciseQueue['uuid']) => exerciseQueuesApi.close(queueUuid)(), {
    onSuccess: () => {
      if (subject) {
        queryClient.setQueryData(exerciseQueuesApiKeys.active(subject.pk), null);
      }
      setPreviewQueue(null);
      setStep('configure');
      setPreviewErrorMessage(null);
    },
    onError: error => {
      setPreviewErrorMessage(resolveQueueErrorMessage(error));
    },
  });

  const onSubmit = async (values: OnGoInitializeFormValues) => {
    if (!subject) {
      return;
    }
    try {
      await createQueueMutation.mutateAsync({ subject, ...values });
    } catch {
      // Error is already exposed via createQueueMutation.error; swallow to avoid unhandled rejection.
    }
  };

  const onInvalidSubmit = useCallback(() => {
    setSubmitBlockedUntilValid(true);
  }, []);

  const onChangeSettings = () => {
    if (!previewQueue || closeQueueMutation.isLoading) {
      return;
    }
    closeQueueMutation.mutate(previewQueue.uuid);
  };

  const onStartSession = () => {
    if (!subject || !previewQueue) {
      return;
    }
    queryClient.setQueryData(exerciseQueuesApiKeys.active(subject.pk), previewQueue);
    setPreviewQueue(null);
    setStep('configure');
  };

  const isSubmitting = createQueueMutation.isLoading;
  const startDisabled = (submitBlockedUntilValid && !isValid) || isSubmitting;
  const configureErrorMessage = createQueueMutation.isError ? resolveQueueErrorMessage(createQueueMutation.error) : null;

  if (step === 'preview' && previewQueue) {
    return (
      <OnGoQueuePreview
        errorMessage={previewErrorMessage}
        formValues={getValues()}
        isChanging={closeQueueMutation.isLoading}
        isStarting={false}
        onChangeSettings={onChangeSettings}
        onStart={onStartSession}
        queue={previewQueue}
        sectionsData={sectionsData}
      />
    );
  }

  return (
    <OnGoLayout
      footer={
        <Button
          color='primary'
          disabled={startDisabled}
          endIcon={isSubmitting ? <CircularProgress color='inherit' size={18} /> : undefined}
          form={ONGO_INIT_FORM_ID}
          fullWidth
          size='large'
          type='submit'
          variant='contained'
        >
          {isSubmitting ? 'Tworzymy kolejkę…' : 'Dalej'}
        </Button>
      }
    >
      <FormErrorBanner message={configureErrorMessage} />
      <form id={ONGO_INIT_FORM_ID} noValidate onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}>
        <OnGoInitializeFormContent
          control={control}
          getValues={getValues}
          isFreePlan={isFreePlan}
          sectionsData={sectionsData}
          setValue={setValue}
          trigger={trigger}
        />
      </form>
    </OnGoLayout>
  );
};

export default OnGoInitialize;
